// ============================================================
// HARMONIC CHARTER — Nashville Number System Parser
// ============================================================

import type {
  Chord,
  Degree,
  ChordQuality,
  Extension,
  NormalizedAccidental,
  ParseResult,
} from '../types';
import { isValidDegree } from '../theory';

const QUALITY_TOKENS: Array<[string, ChordQuality]> = [
  ['mMaj7',  'minorMajor7'],
  ['m7b5',   'halfDiminished'],
  ['maj7',   'major7'],
  ['min7',   'minor7'],
  ['m7',     'minor7'],
  ['dim7',   'diminished'],
  ['dim',    'diminished'],
  ['aug',    'augmented'],
  ['sus4',   'sus4'],
  ['sus2',   'sus2'],
  ['m9',     'minor9'],
  ['maj9',   'major9'],
  ['9',      'dominant9'],
  ['m',      'minor'],
  ['7',      'dominant7'],
];

const EXTENSION_TOKENS: Array<[string, Extension]> = [
  ['add9',  'add9'],
  ['add11', 'add11'],
  ['add13', 'add13'],
  ['b9',    'b9'],
  ['#9',    '#9'],
  ['#11',   '#11'],
  ['b13',   'b13'],
  ['11',    'add11'],
  ['13',    'add13'],
];

export const HOLD_TOKEN = '.';

// ------------------------------------------------------------
// HELPERS
// ------------------------------------------------------------

function parseAccidental(input: string, pos: number): [NormalizedAccidental, number] {
  if (input[pos] === 'b') return ['flat',  pos + 1];
  if (input[pos] === '#') return ['sharp', pos + 1];
  return [null, pos];
}

function parseDegree(input: string, pos: number): [Degree | null, number] {
  const n = parseInt(input[pos], 10);
  if (isValidDegree(n)) return [n as Degree, pos + 1];
  return [null, pos];
}

function parseQuality(input: string, pos: number): [ChordQuality, number] {
  for (const [token, quality] of QUALITY_TOKENS) {
    if (input.startsWith(token, pos)) return [quality, pos + token.length];
  }
  return ['major', pos];
}

function parseExtensions(input: string, pos: number): [Extension[], number] {
  const extensions: Extension[] = [];
  let current = pos;
  while (current < input.length) {
    let matched = false;
    for (const [token, ext] of EXTENSION_TOKENS) {
      if (input.startsWith(token, current)) {
        extensions.push(ext);
        current += token.length;
        matched = true;
        break;
      }
    }
    if (!matched) break;
  }
  return [extensions, current];
}

function parseSlash(input: string, pos: number): [string | null, number] {
  if (input[pos] !== '/') return [null, pos];

  let current = pos + 1;
  const [accidental, afterAcc] = parseAccidental(input, current);
  current = afterAcc;

  const [degree, afterDeg] = parseDegree(input, current);
  if (degree === null) return [null, pos]; // malformed slash, ignore

  // Serialize as string to satisfy SlashBass = string | null
  return [JSON.stringify({ degree, accidental }), afterDeg];
}

// ------------------------------------------------------------
// MAIN EXPORT
// ------------------------------------------------------------

export function parseNashville(token: string): ParseResult {
  const input = token.trim();
  if (input === '') return { ok: false, error: 'Empty token', raw: token };

  let pos = 0;

  const [accidental, afterAcc] = parseAccidental(input, pos);
  pos = afterAcc;

  const [degree, afterDeg] = parseDegree(input, pos);
  if (degree === null) {
    return {
      ok: false,
      error: `Expected scale degree (1–7), got '${input[pos] ?? 'end'}'`,
      raw: token,
    };
  }
  pos = afterDeg;

  const [quality, afterQuality] = parseQuality(input, pos);
  pos = afterQuality;

  const [extensions, afterExt] = parseExtensions(input, pos);
  pos = afterExt;

  const [slash, afterSlash] = parseSlash(input, pos);
  pos = afterSlash;

  if (pos < input.length) {
    return {
      ok: false,
      error: `Unexpected characters '${input.slice(pos)}' after chord`,
      raw: token,
    };
  }

  const chord: Chord = {
    degree,
    accidental,
    quality,
    extensions,
    slash,
    position: 0,
    borrowed: false,
  };

  return { ok: true, chord };
}

export function looksLikeNashville(token: string): boolean {
  return /^[b#]?[1-7]/.test(token.trim());
}